import React, { useState, useEffect } from 'react';
import { loucaApi } from '../../services/loucaApi';

/**
 * Panneau de configuration complet pour V9
 * Inclut : modèles LLM, Florence, paramètres V9
 */
const ConfigPanel = ({ config, onConfigChange }) => {
  const [llmModels, setLlmModels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadConfig();
  }, []);

  const loadConfig = async () => {
    try {
      setLoading(true);
      const [configData, modelsData] = await Promise.all([
        loucaApi.getConfigV9(),
        loucaApi.getLLMModels(),
      ]);

      if (modelsData.status === 'success') {
        setLlmModels(modelsData.models || []);
      }

      if (configData.status === 'success' && configData.config) {
        onConfigChange(configData.config);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (key, value) => {
    onConfigChange({
      ...config,
      [key]: value,
    });
  };

  if (loading) {
    return (
      <div className="bg-slate-800 border border-slate-700 rounded-2xl p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-4 bg-slate-700 rounded w-1/4"></div>
          <div className="h-10 bg-slate-700 rounded"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-900/20 border border-red-500/30 rounded-2xl p-6">
        <p className="text-red-400">Erreur: {error}</p>
        <button
          onClick={loadConfig}
          className="mt-4 px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg"
        >
          Réessayer
        </button>
      </div>
    );
  }

  const currentStep1Model = config?.v9_step1_model || 'gemini-2.5-flash-lite';
  const currentStep2Model = config?.v9_step2_model || 'gemini-2.5-flash';

  return (
    <div className="bg-slate-800 border border-slate-700 rounded-2xl p-6 space-y-6">
      <div>
        <h2 className="text-xl font-semibold text-white mb-2">Configuration V9</h2>
        <p className="text-sm text-slate-400">Paramètres du workflow en 2 étapes</p>
      </div>

      {/* Enable V9 */}
      <div className="flex items-center justify-between p-4 bg-slate-900/60 rounded-xl border border-slate-700/60">
        <div>
          <label className="text-white font-medium">Activer V9</label>
          <p className="text-sm text-slate-400">Workflow en 2 étapes LLM</p>
        </div>
        <label className="relative inline-flex items-center cursor-pointer">
          <input
            type="checkbox"
            checked={config?.enable_v9 !== false}
            onChange={(e) => handleChange('enable_v9', e.target.checked)}
            className="sr-only peer"
          />
          <div className="w-11 h-6 bg-slate-700 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-orange-500/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-orange-500"></div>
        </label>
      </div>

      {/* Step 1 Model */}
      <div className="space-y-2">
        <label className="text-white font-medium">Modèle Step 1 (Détection)</label>
        <p className="text-sm text-slate-400">Modèle rapide pour détecter la présence de dégâts</p>
        <select
          value={currentStep1Model}
          onChange={(e) => handleChange('v9_step1_model', e.target.value)}
          className="w-full px-4 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-orange-500"
        >
          {llmModels
            .filter(m => m.id.includes('flash-lite') || m.id.includes('lite'))
            .map((model) => (
              <option key={model.id} value={model.id}>
                {model.icon} {model.name} - {model.description}
              </option>
            ))}
        </select>
      </div>

      {/* Step 1 Thinking */}
      <div className="flex items-center justify-between p-4 bg-slate-900/60 rounded-xl border border-slate-700/60">
        <div>
          <label className="text-white font-medium">Thinking Step 1</label>
          <p className="text-sm text-slate-400">Mode thinking pour Step 1</p>
        </div>
        <select
          value={config?.v9_step1_thinking || 'off'}
          onChange={(e) => handleChange('v9_step1_thinking', e.target.value)}
          className="px-4 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white text-sm"
        >
          <option value="off">Off</option>
          <option value="on">On</option>
        </select>
      </div>

      {/* Step 2 Model */}
      <div className="space-y-2">
        <label className="text-white font-medium">Modèle Step 2 (Description)</label>
        <p className="text-sm text-slate-400">Modèle performant pour description détaillée</p>
        <select
          value={currentStep2Model}
          onChange={(e) => handleChange('v9_step2_model', e.target.value)}
          className="w-full px-4 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-orange-500"
        >
          {llmModels
            .filter(m => !m.id.includes('lite'))
            .map((model) => (
              <option key={model.id} value={model.id}>
                {model.icon} {model.name} - {model.description}
              </option>
            ))}
        </select>
      </div>

      {/* Step 2 Thinking */}
      <div className="flex items-center justify-between p-4 bg-slate-900/60 rounded-xl border border-slate-700/60">
        <div>
          <label className="text-white font-medium">Thinking Step 2</label>
          <p className="text-sm text-slate-400">Mode thinking pour Step 2 (recommandé: On)</p>
        </div>
        <select
          value={config?.v9_step2_thinking || 'on'}
          onChange={(e) => handleChange('v9_step2_thinking', e.target.value)}
          className="px-4 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white text-sm"
        >
          <option value="off">Off</option>
          <option value="on">On</option>
        </select>
      </div>

      {/* Florence (si disponible) */}
      <div className="flex items-center justify-between p-4 bg-slate-900/60 rounded-xl border border-slate-700/60">
        <div>
          <label className="text-white font-medium">Florence</label>
          <p className="text-sm text-slate-400">Activer le modèle Florence (si disponible)</p>
        </div>
        <label className="relative inline-flex items-center cursor-pointer">
          <input
            type="checkbox"
            checked={config?.enable_florence === true}
            onChange={(e) => handleChange('enable_florence', e.target.checked)}
            className="sr-only peer"
          />
          <div className="w-11 h-6 bg-slate-700 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-orange-500/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-orange-500"></div>
        </label>
      </div>

      {/* Extraction Method */}
      <div className="space-y-2">
        <label className="text-white font-medium">Méthode d'extraction</label>
        <p className="text-sm text-slate-400">KMeans ou HAC pour sélectionner les frames</p>
        <select
          value={config?.extraction_method || 'HAC_COSINE'}
          onChange={(e) => handleChange('extraction_method', e.target.value)}
          className="w-full px-4 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-orange-500"
        >
          <option value="KMEANS">KMeans</option>
          <option value="HAC_COSINE">HAC (Hierarchical Agglomerative Clustering)</option>
        </select>
      </div>

      {/* HAC Extraction Threshold */}
      {config?.extraction_method === 'HAC_COSINE' && (
        <div className="space-y-2">
          <label className="text-white font-medium">Seuil HAC Extraction</label>
          <p className="text-sm text-slate-400">Seuil de similarité pour HAC (0.0-1.0)</p>
          <input
            type="number"
            step="0.01"
            value={config?.hac_extraction_threshold || 0.35}
            onChange={(e) => handleChange('hac_extraction_threshold', parseFloat(e.target.value))}
            className="w-full px-4 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white"
            min="0"
            max="1"
          />
        </div>
      )}

      {/* Déduplication Method */}
      <div className="space-y-2">
        <label className="text-white font-medium">Méthode de déduplication</label>
        <p className="text-sm text-slate-400">SIMPLE ou HAC pour éliminer les doublons</p>
        <select
          value={config?.deduplication_method || 'SIMPLE'}
          onChange={(e) => handleChange('deduplication_method', e.target.value)}
          className="w-full px-4 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-orange-500"
        >
          <option value="SIMPLE">SIMPLE</option>
          <option value="HAC_COSINE">HAC_COSINE</option>
        </select>
      </div>

      {/* HAC Deduplication Threshold */}
      {config?.deduplication_method === 'HAC_COSINE' && (
        <div className="space-y-2">
          <label className="text-white font-medium">Seuil HAC Déduplication</label>
          <p className="text-sm text-slate-400">Seuil de similarité pour déduplication (0.0-1.0)</p>
          <input
            type="number"
            step="0.01"
            value={config?.hac_deduplication_threshold || 0.25}
            onChange={(e) => handleChange('hac_deduplication_threshold', parseFloat(e.target.value))}
            className="w-full px-4 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white"
            min="0"
            max="1"
          />
        </div>
      )}

      {/* Paramètres avancés */}
      <details className="bg-slate-900/60 rounded-xl border border-slate-700/60">
        <summary className="px-4 py-3 cursor-pointer text-orange-300 font-medium">
          Paramètres avancés
        </summary>
        <div className="p-4 space-y-4">
          {/* Sample FPS */}
          <div className="space-y-2">
            <label className="text-white text-sm">Sample FPS</label>
            <input
              type="number"
              value={config?.sample_fps || 2}
              onChange={(e) => handleChange('sample_fps', parseInt(e.target.value))}
              className="w-full px-4 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white"
              min="1"
              max="10"
            />
          </div>

          {/* Blur Threshold */}
          <div className="space-y-2">
            <label className="text-white text-sm">Blur Threshold</label>
            <input
              type="number"
              value={config?.blur_threshold || 100}
              onChange={(e) => handleChange('blur_threshold', parseInt(e.target.value))}
              className="w-full px-4 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white"
              min="0"
              max="500"
            />
          </div>

          {/* Blur Thresholds par résolution */}
          <details className="bg-slate-950 rounded-lg p-3">
            <summary className="text-xs text-slate-400 cursor-pointer">Seuils flou par résolution</summary>
            <div className="mt-3 space-y-2">
              <div className="space-y-1">
                <label className="text-white text-xs">Low Res</label>
                <input
                  type="number"
                  value={config?.blur_threshold_low_res || ''}
                  onChange={(e) => handleChange('blur_threshold_low_res', e.target.value ? parseInt(e.target.value) : null)}
                  placeholder="Optionnel"
                  className="w-full px-3 py-1 bg-slate-900 border border-slate-700 rounded text-white text-xs"
                />
              </div>
              <div className="space-y-1">
                <label className="text-white text-xs">Medium Res</label>
                <input
                  type="number"
                  value={config?.blur_threshold_medium_res || ''}
                  onChange={(e) => handleChange('blur_threshold_medium_res', e.target.value ? parseInt(e.target.value) : null)}
                  placeholder="Optionnel"
                  className="w-full px-3 py-1 bg-slate-900 border border-slate-700 rounded text-white text-xs"
                />
              </div>
              <div className="space-y-1">
                <label className="text-white text-xs">High Res</label>
                <input
                  type="number"
                  value={config?.blur_threshold_high_res || ''}
                  onChange={(e) => handleChange('blur_threshold_high_res', e.target.value ? parseInt(e.target.value) : null)}
                  placeholder="Optionnel"
                  className="w-full px-3 py-1 bg-slate-900 border border-slate-700 rounded text-white text-xs"
                />
              </div>
            </div>
          </details>

          {/* Extraction Count */}
          <div className="space-y-2">
            <label className="text-white text-sm">Extraction Count</label>
            <input
              type="number"
              value={config?.extraction_count || 20}
              onChange={(e) => handleChange('extraction_count', parseInt(e.target.value))}
              className="w-full px-4 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white"
              min="1"
              max="100"
            />
          </div>

          {/* Min Confidence Car */}
          <div className="space-y-2">
            <label className="text-white text-sm">Min Confidence Car</label>
            <input
              type="number"
              step="0.01"
              value={config?.min_confidence_car || 0.1}
              onChange={(e) => handleChange('min_confidence_car', parseFloat(e.target.value))}
              className="w-full px-4 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white"
              min="0"
              max="1"
            />
          </div>

          {/* Grid Parameters */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <label className="text-white text-sm">Grid Columns</label>
              <input
                type="number"
                value={config?.grid_cols || 10}
                onChange={(e) => handleChange('grid_cols', parseInt(e.target.value))}
                className="w-full px-4 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white"
                min="5"
                max="20"
              />
            </div>
            <div className="space-y-2">
              <label className="text-white text-sm">Grid Rows</label>
              <input
                type="number"
                value={config?.grid_rows || 10}
                onChange={(e) => handleChange('grid_rows', parseInt(e.target.value))}
                className="w-full px-4 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white"
                min="5"
                max="20"
              />
            </div>
          </div>
        </div>
      </details>

      {/* Florence Parameters */}
      {config?.enable_florence && (
        <details className="bg-slate-900/60 rounded-xl border border-slate-700/60">
          <summary className="px-4 py-3 cursor-pointer text-orange-300 font-medium">
            Paramètres Florence
          </summary>
          <div className="p-4 space-y-4">
            <div className="space-y-2">
              <label className="text-white text-sm">Florence Model ID</label>
              <input
                type="text"
                value={config?.florence_model_id || 'microsoft/Florence-2-large-ft'}
                onChange={(e) => handleChange('florence_model_id', e.target.value)}
                className="w-full px-4 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white"
              />
            </div>
            <div className="space-y-2">
              <label className="text-white text-sm">Florence Prompt</label>
              <textarea
                value={config?.florence_prompt || ''}
                onChange={(e) => handleChange('florence_prompt', e.target.value)}
                className="w-full px-4 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white"
                rows="3"
                placeholder="dent on car door. deep scratch. crumpled metal. impact damage."
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="text-white text-sm">Max Tokens</label>
                <input
                  type="number"
                  value={config?.florence_max_tokens || 1024}
                  onChange={(e) => handleChange('florence_max_tokens', parseInt(e.target.value))}
                  className="w-full px-4 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white"
                  min="1"
                  max="4096"
                />
              </div>
              <div className="space-y-2">
                <label className="text-white text-sm">Num Beams</label>
                <input
                  type="number"
                  value={config?.florence_num_beams || 3}
                  onChange={(e) => handleChange('florence_num_beams', parseInt(e.target.value))}
                  className="w-full px-4 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white"
                  min="1"
                  max="10"
                />
              </div>
            </div>
          </div>
        </details>
      )}

      {/* Log Detail Level */}
      <div className="space-y-2">
        <label className="text-white font-medium">Niveau de détail des logs</label>
        <p className="text-sm text-slate-400">Contrôle la quantité d'informations affichées dans la console</p>
        <select
          value={config?.log_detail_level || 'standard'}
          onChange={(e) => handleChange('log_detail_level', e.target.value)}
          className="w-full px-4 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-orange-500"
        >
          <option value="minimal">Minimal - Messages uniquement</option>
          <option value="standard">Standard - Messages + Inputs/Outputs</option>
          <option value="verbose">Verbose - Standard + Params</option>
          <option value="debug">Debug - Tout (Verbose + Raw Data)</option>
        </select>
      </div>

      {/* Debug Options */}
      <details className="bg-slate-900/60 rounded-xl border border-slate-700/60">
        <summary className="px-4 py-3 cursor-pointer text-orange-300 font-medium">
          Options Debug
        </summary>
        <div className="p-4 space-y-3">
          <div className="flex items-center justify-between">
            <div>
              <label className="text-white text-sm">Debug Mode</label>
              <p className="text-xs text-slate-400">Activer les logs détaillés</p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={config?.debug === true}
                onChange={(e) => handleChange('debug', e.target.checked)}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-slate-700 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-orange-500/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-orange-500"></div>
            </label>
          </div>
          <div className="flex items-center justify-between">
            <div>
              <label className="text-white text-sm">Afficher frames initiales</label>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={config?.display_initial_frames === true}
                onChange={(e) => handleChange('display_initial_frames', e.target.checked)}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-slate-700 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-orange-500/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-orange-500"></div>
            </label>
          </div>
          <div className="flex items-center justify-between">
            <div>
              <label className="text-white text-sm">Afficher rejetées (flou)</label>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={config?.display_rejected_blur === true}
                onChange={(e) => handleChange('display_rejected_blur', e.target.checked)}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-slate-700 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-orange-500/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-orange-500"></div>
            </label>
          </div>
          <div className="flex items-center justify-between">
            <div>
              <label className="text-white text-sm">Afficher rejetées (pas voiture)</label>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={config?.display_rejected_not_car === true}
                onChange={(e) => handleChange('display_rejected_not_car', e.target.checked)}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-slate-700 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-orange-500/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-orange-500"></div>
            </label>
          </div>
          <div className="flex items-center justify-between">
            <div>
              <label className="text-white text-sm">Afficher rejetées (doublons)</label>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={config?.display_rejected_duplicates === true}
                onChange={(e) => handleChange('display_rejected_duplicates', e.target.checked)}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-slate-700 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-orange-500/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-orange-500"></div>
            </label>
          </div>
        </div>
      </details>
    </div>
  );
};

export default ConfigPanel;

